/**
 * Shared rules for proposal upload / import recovery (legacy + project option flows).
 * Parser output is advisory; human confirmation + line data gate save.
 */

export const UPLOAD_LOW_CONFIDENCE_THRESHOLD = 0.55;
export const UPLOAD_CRITICAL_CONFIDENCE_FLOOR = 0.35;

/** Single canonical summary line description for recovered imports (44PU-4). */
export const PROPOSAL_SUMMARY_LINE_DESCRIPTION = "Imported proposal / Proposal summary";

export type UploadRecoveryLineItem = {
  rawText: string;
  name: string;
  description: string;
  brand: string | null;
  model: string | null;
  category: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  extractionConfidence: number;
  /** Cleo visible product description (DTOOLS JSON import). */
  cleoDescription?: string | null;
  /** Cleo hover explanation JSON (DTOOLS JSON import). */
  customerExplanation?: string | null;
};

export function humanizeParserBlockReason(code: string): string {
  const c = String(code ?? "").trim();
  if (!c) return "Parser flagged this import for review.";
  switch (c) {
    case "NO_VALID_LINE_ITEMS":
      return "No product line items were detected automatically — add lines manually or use a summary line.";
    case "PARSER_FAILURE_DOUBLE":
      return "Automated parsing could not complete cleanly — use manual lines or a summary from total.";
    case "TOTALS_MISMATCH_CRITICAL":
      return "Header total and line totals disagree materially — check amounts before saving.";
    case "CONFIDENCE_CRITICAL":
      return "Extraction confidence is critically low — review carefully before saving.";
    default:
      return c.replace(/_/g, " ").toLowerCase().replace(/^\w/, (x) => x.toUpperCase());
  }
}

export function lowConfidenceAckRequired(parseConfidence: number | undefined): boolean {
  const c = Number(parseConfidence ?? 1);
  return Number.isFinite(c) && c < UPLOAD_LOW_CONFIDENCE_THRESHOLD;
}

export function resolvedGrandTotalFromMetaAndItems(
  grandTotalMeta: number | undefined,
  items: Array<{ totalPrice?: number }>,
): number {
  if (typeof grandTotalMeta === "number" && Number.isFinite(grandTotalMeta) && grandTotalMeta > 0) {
    return grandTotalMeta;
  }
  const sum = items.reduce((s, row) => s + Number(row.totalPrice || 0), 0);
  return typeof sum === "number" && Number.isFinite(sum) && sum > 0 ? sum : 0;
}

/**
 * @param mode `legacy` = Upload proposal dialog (SAPO-style blank row). `default` = project option wizard.
 */
export function createEmptyManualUploadLineItem(mode: "default" | "legacy" = "default"): UploadRecoveryLineItem {
  const desc = mode === "legacy" ? "Imported proposal line" : "New line item";
  return {
    rawText: mode === "legacy" ? "Imported proposal line" : "Manual line item",
    name: desc,
    description: desc,
    brand: null,
    model: null,
    category: null,
    quantity: 1,
    unitPrice: 0,
    totalPrice: 0,
    extractionConfidence: 1,
  };
}

export function createSummaryUploadLineItemFromTotal(total: number): UploadRecoveryLineItem {
  const t = Math.max(0, Number(total) || 0);
  const desc = PROPOSAL_SUMMARY_LINE_DESCRIPTION;
  return {
    rawText: desc,
    name: desc,
    description: desc,
    brand: null,
    model: null,
    category: null,
    quantity: 1,
    unitPrice: t,
    totalPrice: t,
    extractionConfidence: 1,
  };
}
